import React, { useRef, useState } from 'react';
import hoverIcon from '../assets/hover.png';
import { useLibraryData } from '../hooks/useLibraryData';

interface ThumbnailCardProps {
  exhibitorSlug: string;
  librarySlug: string;
  onClick?: () => void;
  href?: string;
  mode?: 'modal' | 'link';
}

export const ThumbnailCard: React.FC<ThumbnailCardProps> = ({
  exhibitorSlug,
  librarySlug,
  onClick,
  href,
  mode = 'modal'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { data, loading } = useLibraryData(exhibitorSlug, librarySlug);
  const isLinkMode = mode === 'link' && Boolean(href);
  const hiddenLinkRef = useRef<HTMLAnchorElement | null>(null);

  const commonProps = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    style: {
      position: 'relative' as const,
      width: '100%',
      display: 'inline-block',
      borderRadius: '5px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
      boxShadow: isHovered 
        ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    }
  };

  const handleCardClick = () => {
    if (isLinkMode && href) {
      hiddenLinkRef.current?.click();
      return;
    }

    onClick?.();
  };

  return (
    <div {...commonProps} onClick={handleCardClick}>
      {isLinkMode && href && (
        <a
          ref={hiddenLinkRef}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
        />
      )}
      {/* サムネイル画像 */}
      {!loading && data?.thumbnail && (
        <img 
          src={data.thumbnail}
          alt=""
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            maxWidth: '100%'
          }}
        />
      )}
      
      {/* フォールバック表示は行わず、レスポンス待ちやエラー時は非表示 */}

      {/* ホバー時のオーバーレイ */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(var(--overlay-color, 51, 51, 51), 0.95)',
          borderRadius: '5px',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s ease',
          pointerEvents: 'none'
        }}
      />

      {/* 常時表示されるプレイボタンアイコン */}
      {!loading && data?.thumbnail && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100px',
            height: '100px',
            background: 'rgba(var(--overlay-color, 51, 51, 51), 0.95)',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            ...(isHovered && {
              transform: 'translate(-50%, -50%) scale(1.1)',
              background: 'rgba(var(--overlay-color, 51, 51, 51), 0.0)'
            })
          }}
        >
          <img 
            src={hoverIcon} 
            alt="View details"
            style={{
              width: '80px',
              // height: '80px',
              filter: 'brightness(0) invert(1)'
            }}
          />
        </div>
      )}


      {/* アニメーション用のスタイル */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.5); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
      `}</style>
    </div>
  );
};
